import { useMemo } from "react";
import { useDeptTreeList } from "@/hooks/services";
import type { PersonnelItem, OperationEvent } from "../type";

// ==================== 类型定义 ====================
type UseModalBodyParams = {
  searchedList: PersonnelItem[];
  eventType: OperationEvent;
  selectedDeptNo: string;
};

// ==================== 工具函数 ====================
/**
 * 从树形数据中递归查找部门名称
 */
const findDeptNameInTree = (
  treeData: any[],
  targetKey: string,
): string | null => {
  for (const node of treeData) {
    if (String(node.key) === targetKey) {
      return node.title;
    }
    if (node.children && node.children.length > 0) {
      const found = findDeptNameInTree(node.children, targetKey);
      if (found) return found;
    }
  }
  return null;
};

// ==================== Hook ====================
export function useModalBody({
  searchedList,
  eventType,
  selectedDeptNo,
}: UseModalBodyParams) {
  const { deptTreeData } = useDeptTreeList();

  // 根据事件类型和部门选择决定显示的人员列表
  const displayPersonnelList = useMemo(() => {
    if (eventType === "search") {
      // 搜索状态：显示搜索结果
      return searchedList;
    } else if (eventType === "deptSelect" && selectedDeptNo) {
      // 部门选择状态：显示该部门的所有员工（由 searchedList 承载）
      return searchedList;
    }
    return [];
  }, [searchedList, eventType, selectedDeptNo]);

  // 获取选中部门的名称
  const selectedDeptName = useMemo(() => {
    if (
      eventType === "deptSelect" &&
      selectedDeptNo &&
      deptTreeData.length > 0
    ) {
      return findDeptNameInTree(deptTreeData, selectedDeptNo);
    }
    return null;
  }, [eventType, selectedDeptNo, deptTreeData]);

  return {
    displayPersonnelList,
    selectedDeptName,
  };
}
